import { createFileRoute, Link } from '@tanstack/react-router';
import { useContentTypes } from '@wordpuppi/shared';
import { LoadingSpinner } from '@/components/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { TypeIcon } from '@/components/TypeIcon';
import { Plus, Table2 } from 'lucide-react';

export const Route = createFileRoute('/sites/$siteSlug/content-types/tables/')({
  component: TablesPage,
});

function TablesPage() {
  const { siteSlug } = Route.useParams();
  const { data, isLoading, error } = useContentTypes(siteSlug);

  // Filter to only show custom types (not system types)
  const customTypes = data?.data?.filter((t) => !t.is_system) || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tables</h1>
      </div>

      <p className="text-muted-foreground mb-6">
        Browse and manage data in your custom content types.
      </p>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">
          Error loading content types
        </div>
      ) : customTypes.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-xl border-2 border-dashed border-border">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <Table2 className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-lg font-medium">No tables yet</p>
          <p className="text-muted-foreground mt-1 mb-6">
            Create a custom type first, then its data lives here as a table.
          </p>
          <Button asChild>
            <Link to="/sites/$siteSlug/content-types/new" params={{ siteSlug }}>
              <Plus />
              Create Custom Type
            </Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {customTypes.map((type) => (
            <Link
              key={type.id}
              to="/sites/$siteSlug/content-types/tables/$typeSlug"
              params={{ siteSlug, typeSlug: type.slug }}
              className="flex items-center gap-4 p-5 bg-card border rounded-lg hover:border-primary/50 transition-colors"
            >
              <div className="text-3xl w-12 h-12 shrink-0 flex items-center justify-center bg-muted rounded-lg">
                <TypeIcon icon={type.icon} className="size-6 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{type.name}</h3>
                <p className="text-xs text-muted-foreground font-mono">{type.slug}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {type.item_count} {type.item_count === 1 ? 'item' : 'items'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
